/**
 * Leviathan Web - Accounts Receivable Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 *
 * Past due / open AR dashboard with aging breakdown
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { API_URL } from '@/config/api';
import { Link } from 'react-router-dom';

/* ───────────────── Interfaces ───────────────── */

interface ARInvoice {
  id: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  status: string;
  total: number;
  amountPaid: number;
  amountDue: number;
  daysPastDue: number;
  agingBucket: string;
  customer: {
    id: string;
    name: string;
    customerNumber: string;
  };
}

interface ARSummary {
  totalOutstanding: number;
  totalCount: number;
  pastDueAmount: number;
  pastDueCount: number;
  aging: {
    current: { count: number; amount: number };
    '1-30': { count: number; amount: number };
    '31-60': { count: number; amount: number };
    '61-90': { count: number; amount: number };
    '90+': { count: number; amount: number };
  };
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

/* ───────────────── Helpers ───────────────── */

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

function getStatusColor(status: string): string {
  switch (status) {
    case 'SENT':
      return 'text-amber bg-amber/10';
    case 'VIEWED':
      return 'text-amber bg-amber/10';
    case 'PARTIAL':
      return 'text-warning-amber bg-warning-amber/10';
    case 'OVERDUE':
      return 'text-error-red bg-error-red/10';
    case 'PENDING':
      return 'text-steel bg-steel/10';
    default:
      return 'text-text-secondary bg-text-secondary/10';
  }
}

function getAgeBadgeColor(days: number): string {
  if (days === 0) return 'text-success-green';
  if (days <= 30) return 'text-amber';
  if (days <= 60) return 'text-warning-amber';
  return 'text-error-red';
}

function getBucketColor(bucket: string): string {
  switch (bucket) {
    case 'current':
      return 'border-success-green';
    case '1-30':
      return 'border-amber';
    case '31-60':
      return 'border-warning-amber';
    case '61-90':
      return 'border-error-red';
    case '90+':
      return 'border-error-red';
    default:
      return 'border-panel-border';
  }
}

function getBucketTextColor(bucket: string): string {
  switch (bucket) {
    case 'current':
      return 'text-success-green';
    case '1-30':
      return 'text-amber';
    case '31-60':
      return 'text-warning-amber';
    case '61-90':
      return 'text-error-red';
    case '90+':
      return 'text-error-red';
    default:
      return 'text-text-secondary';
  }
}

/* ───────────────── Stat Card ───────────────── */

function StatCard({
  label,
  value,
  status = 'neutral',
}: {
  label: string;
  value: string;
  status?: 'neutral' | 'warning' | 'critical';
}) {
  const borderColor =
    status === 'critical'
      ? 'border-error-red'
      : status === 'warning'
        ? 'border-warning-amber'
        : 'border-panel-border';

  const valueColor =
    status === 'critical'
      ? 'text-error-red'
      : status === 'warning'
        ? 'text-warning-amber'
        : 'text-amber';

  return (
    <div className={`relative bg-panel-dark border ${borderColor} p-4`}>
      <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber" />
      <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber" />

      <div className="text-text-tertiary text-xs uppercase tracking-widest font-mono mb-2">
        {label}
      </div>
      <div
        className={`text-xl font-mono font-bold ${valueColor}`}
        style={{ textShadow: '0 0 10px currentColor' }}
      >
        {value}
      </div>
    </div>
  );
}

/* ───────────────── Aging Bucket Card ───────────────── */

function AgingBucketCard({
  label,
  bucket,
  count,
  amount,
  isActive,
  onClick,
}: {
  label: string;
  bucket: string;
  count: number;
  amount: number;
  isActive: boolean;
  onClick: () => void;
}) {
  const borderColor = getBucketColor(bucket);
  const textColor = getBucketTextColor(bucket);

  return (
    <button
      onClick={onClick}
      className={`relative bg-panel-dark border p-3 text-left transition-all w-full ${
        isActive
          ? `${borderColor} ring-1 ring-current bg-amber/5`
          : 'border-panel-border hover:border-amber/50'
      }`}
    >
      <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber" />
      <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber" />

      <div className="text-text-tertiary text-[10px] uppercase tracking-widest font-mono mb-1">
        {label}
      </div>
      <div
        className={`text-lg font-mono font-bold ${textColor}`}
        style={{ textShadow: '0 0 8px currentColor' }}
      >
        {formatCurrency(amount)}
      </div>
      <div className="text-text-tertiary text-xs font-mono mt-1">
        {count} invoice{count !== 1 ? 's' : ''}
      </div>
    </button>
  );
}

/* ───────────────── Main Component ───────────────── */

const AccountsReceivablePage = () => {
  const { accessToken } = useAuth();

  // Data state
  const [invoices, setInvoices] = useState<ARInvoice[]>([]);
  const [summary, setSummary] = useState<ARSummary | null>(null);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  // Filter state
  const [statusFilter, setStatusFilter] = useState<'all' | 'past-due'>('all');
  const [agingBucket, setAgingBucket] = useState<string>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(true);

  /* ── Fetch AR Summary ── */
  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/analytics/ar-summary`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      }
    } catch (error) {
      console.error('Failed to fetch AR summary:', error);
    } finally {
      setSummaryLoading(false);
    }
  }, [accessToken]);

  /* ── Fetch AR Invoices ── */
  const fetchInvoices = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (agingBucket) params.append('agingBucket', agingBucket);
      if (search) params.append('search', search);
      params.append('page', page.toString());
      params.append('limit', '20');

      const response = await fetch(
        `${API_URL}/api/invoices/accounts-receivable?${params}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setInvoices(data.data);
        setPagination(data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch AR invoices:', error);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, statusFilter, agingBucket, search, page]);

  /* ── Effects ── */
  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [statusFilter, agingBucket, search]);

  /* ── Bucket click handler ── */
  const handleBucketClick = (bucket: string) => {
    setAgingBucket((prev) => (prev === bucket ? '' : bucket));
  };

  /* ── Derived display status ── */
  const getDisplayStatus = (inv: ARInvoice) => {
    if (inv.daysPastDue > 0 && !['PAID', 'VOID', 'WRITTEN_OFF'].includes(inv.status)) {
      return 'PAST DUE';
    }
    return inv.status;
  };

  const getDisplayStatusColor = (inv: ARInvoice) => {
    const display = getDisplayStatus(inv);
    if (display === 'PAST DUE') return 'text-error-red bg-error-red/10';
    return getStatusColor(inv.status);
  };

  /* ───────────────── Render ───────────────── */
  return (
    <div className="space-y-6 relative">
      {/* ── Page Header ── */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                Accounts Receivable
              </h1>
              <p className="text-text-secondary text-sm font-mono mt-1">
                Open AR &amp; Past Due Invoices
              </p>
            </div>
            <Link
              to="/invoices"
              className="flex items-center space-x-2 px-4 py-2 border border-panel-border text-text-secondary hover:text-amber hover:border-amber transition-colors font-mono text-sm uppercase tracking-wider"
            >
              <span>All Invoices</span>
            </Link>
          </div>
        </div>
      </div>

      {/* ── Summary Stat Cards ── */}
      {summaryLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-panel-dark border border-panel-border p-4 animate-pulse">
              <div className="h-4 bg-panel-border rounded w-24 mb-2" />
              <div className="h-6 bg-panel-border rounded w-32" />
            </div>
          ))}
        </div>
      ) : summary ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            label="Total Outstanding"
            value={formatCurrency(summary.totalOutstanding)}
          />
          <StatCard
            label="Past Due Amount"
            value={formatCurrency(summary.pastDueAmount)}
            status={summary.pastDueAmount > 0 ? 'critical' : 'neutral'}
          />
          <StatCard
            label="Past Due Invoices"
            value={summary.pastDueCount.toString()}
            status={summary.pastDueCount > 0 ? 'warning' : 'neutral'}
          />
        </div>
      ) : null}

      {/* ── Aging Buckets ── */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <AgingBucketCard
            label="Current"
            bucket="current"
            count={summary.aging.current.count}
            amount={summary.aging.current.amount}
            isActive={agingBucket === 'current'}
            onClick={() => handleBucketClick('current')}
          />
          <AgingBucketCard
            label="1-30 Days"
            bucket="1-30"
            count={summary.aging['1-30'].count}
            amount={summary.aging['1-30'].amount}
            isActive={agingBucket === '1-30'}
            onClick={() => handleBucketClick('1-30')}
          />
          <AgingBucketCard
            label="31-60 Days"
            bucket="31-60"
            count={summary.aging['31-60'].count}
            amount={summary.aging['31-60'].amount}
            isActive={agingBucket === '31-60'}
            onClick={() => handleBucketClick('31-60')}
          />
          <AgingBucketCard
            label="61-90 Days"
            bucket="61-90"
            count={summary.aging['61-90'].count}
            amount={summary.aging['61-90'].amount}
            isActive={agingBucket === '61-90'}
            onClick={() => handleBucketClick('61-90')}
          />
          <AgingBucketCard
            label="90+ Days"
            bucket="90+"
            count={summary.aging['90+'].count}
            amount={summary.aging['90+'].amount}
            isActive={agingBucket === '90+'}
            onClick={() => handleBucketClick('90+')}
          />
        </div>
      )}

      {/* ── Filters ── */}
      <div className="bg-panel-dark border border-panel-border p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              Search
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Invoice #, customer name..."
              className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm transition-all placeholder:text-text-tertiary"
            />
          </div>
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'past-due')}
              className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm transition-all"
            >
              <option value="all">All Open</option>
              <option value="past-due">Past Due Only</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearch('');
                setStatusFilter('all');
                setAgingBucket('');
                setPage(1);
              }}
              className="w-full bg-panel-border hover:bg-steel/20 text-text-primary font-mono py-2 px-4 transition-all uppercase text-xs tracking-wider border border-panel-border hover:border-steel"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* ── Data Table ── */}
      <div className="relative">
        <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
        <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

        <div className="bg-panel-dark border border-panel-border overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="text-text-tertiary font-mono animate-pulse">
                &gt; Loading accounts receivable...
              </div>
            </div>
          ) : invoices.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-text-tertiary font-mono text-lg mb-2">
                &gt; No open receivables found
              </div>
              <p className="text-text-tertiary font-mono text-sm">
                {search || statusFilter !== 'all' || agingBucket
                  ? 'Try adjusting your filters'
                  : 'All invoices are paid or settled'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-background border-b border-panel-border">
                  <tr>
                    <th className="text-left px-4 py-3 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                      Invoice #
                    </th>
                    <th className="text-left px-4 py-3 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                      Customer
                    </th>
                    <th className="text-left px-4 py-3 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                      Invoice Date
                    </th>
                    <th className="text-left px-4 py-3 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                      Due Date
                    </th>
                    <th className="text-right px-4 py-3 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                      Amount
                    </th>
                    <th className="text-right px-4 py-3 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                      Balance
                    </th>
                    <th className="text-center px-4 py-3 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                      Status
                    </th>
                    <th className="text-right px-4 py-3 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                      Age (days)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => {
                    const displayStatus = getDisplayStatus(inv);
                    const statusColor = getDisplayStatusColor(inv);
                    const isPastDue = inv.daysPastDue > 0;

                    return (
                      <tr
                        key={inv.id}
                        className={`border-b border-panel-border/50 hover:bg-amber/5 transition-colors ${
                          isPastDue ? 'bg-error-red/5' : ''
                        }`}
                      >
                        <td className="px-4 py-3">
                          <Link
                            to={`/invoices/${inv.id}`}
                            className="text-amber hover:text-amber/80 font-mono font-semibold"
                          >
                            {inv.invoiceNumber}
                          </Link>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-text-primary font-mono text-sm">
                            {inv.customer.name}
                          </div>
                          <div className="text-text-tertiary font-mono text-xs">
                            {inv.customer.customerNumber}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-text-secondary font-mono text-sm">
                          {formatDate(inv.invoiceDate)}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`font-mono text-sm ${
                              isPastDue ? 'text-error-red font-semibold' : 'text-text-secondary'
                            }`}
                          >
                            {formatDate(inv.dueDate)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm text-text-primary">
                          {formatCurrency(inv.total)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span
                            className="font-mono font-semibold text-amber"
                            style={{ textShadow: '0 0 10px rgba(255, 149, 0, 0.4)' }}
                          >
                            {formatCurrency(inv.amountDue)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`${statusColor} font-mono text-xs uppercase tracking-wider font-semibold px-2 py-1 rounded`}
                          >
                            {displayStatus}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span
                            className={`font-mono font-bold ${getAgeBadgeColor(inv.daysPastDue)}`}
                          >
                            {inv.daysPastDue}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Pagination ── */}
          {!isLoading && pagination.pages > 1 && (
            <div className="flex items-center justify-between border-t border-panel-border px-4 py-3">
              <div className="text-text-tertiary font-mono text-xs">
                Showing {(pagination.page - 1) * pagination.limit + 1}-
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1 border border-panel-border text-text-secondary hover:text-amber hover:border-amber font-mono text-xs uppercase tracking-wider disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  Prev
                </button>
                {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                  // Show pages around current
                  let pageNum: number;
                  if (pagination.pages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= pagination.pages - 2) {
                    pageNum = pagination.pages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`px-3 py-1 border font-mono text-xs transition-colors ${
                        pageNum === page
                          ? 'border-amber text-amber bg-amber/10'
                          : 'border-panel-border text-text-secondary hover:text-amber hover:border-amber'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                  disabled={page >= pagination.pages}
                  className="px-3 py-1 border border-panel-border text-text-secondary hover:text-amber hover:border-amber font-mono text-xs uppercase tracking-wider disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountsReceivablePage;
